import { isDefined, isNotDefined } from '@sgme/fp';

import { cashApi } from '@/store/cash/api/index.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { StartAppListening } from '@/store/listenerMiddleware.ts';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export const registerCashGetFixingReferencesForNdf = (startAppListening: StartAppListening) => {
  // Listener to fetch ESP limits when currencies are changed in cash tiles
  startAppListening({
    matcher: cashApi.endpoints.checkCashByTradeCapture.matchFulfilled,
    effect: async (action, api) => {
      // request fixing references if
      // - the tile becomes a NDF
      // - the currency pair is changed
      const previousState = api.getOriginalState();
      const currentState = api.getState();

      // Extract tileId from action meta
      const { tileId } = action.meta.arg?.originalArgs || {};

      if (isNotDefined(tileId)) {
        return;
      }

      // Get current tile from previous state
      const currentTile = selectCashTile(previousState, tileId);

      if (isNotDefined(currentTile)) {
        return;
      }

      // Adaptation: payload is now a dictionary of CheckedCash, get the correct one
      const checkedCash = action.payload?.[tileId];

      if (isNotDefined(checkedCash)) {
        return;
      }

      const changedFields = checkedCash.changedFields;
      const properties = changedFields?.legs?.['0'];

      if (isNotDefined(changedFields) || isNotDefined(properties)) {
        return;
      }

      // Use currencyPair from changedFields, fallback to currentTile
      const currencyPair = changedFields.currencyPair ?? currentTile.properties.currencyPair;
      const clientId = selectTileClientId(currentState, tileId);

      if (isNotDefined(currencyPair) || isNotDefined(clientId)) {
        console.warn('=== currencyPair or clientId is not defined', { currencyPair, clientId });
        return;
      }

      // Determine if fixing references should be requested
      const isFixingReferencesRequested =
        (currentTile.type !== 'FxNdf' && properties.productName === 'FxNdf') ||
        (isDefined(changedFields.currencyPair) && currentTile.properties.currencyPair !== changedFields.currencyPair);

      if (!isFixingReferencesRequested) {
        return;
      }

      // Dispatch request for fixing references
      await api.dispatch(cashApi.endpoints.getFixingReferences.initiate({ tileId, currencyPair, clientId }));

      // Call trade capture to update the tile with new fixing source
      const updatedState = api.getState();
      const updatedTile = selectCashTile(updatedState, tileId);

      if (isNotDefined(updatedTile) || updatedTile.type !== 'FxNdf') {
        return;
      }

      const fixingSource = updatedTile.properties.fixingSource;

      if (isNotDefined(updatedTile.tradeCapture)) {
        return;
      }

      await api.dispatch(
        cashApi.endpoints.checkCashByTradeCapture.initiate({
          tileId,
          type: 'FxNdf',
          properties: {
            fixingSource,
          },
        }),
      );
    },
  });
};
